<template>
  <v-row class="filter-box my-0" data-id-e2e="cdtFilterSelect">
    <v-col cols="3" class="ps-0">
      {{ $t('workpackagePage.splitWorkpackages.filterCdt') }}
    </v-col>
    <v-select
      v-model="selectedCdts"
      :items="cdtItems"
      hide-details
      white
      multiple
      item-value="productCategoryKey"
      item-title="description"
      data-id-e2e="cdtSelect"
      class="rtls-select rtls-select--white"
      :disabled="!cdtItems.length"
      :placeholder="$t('filterWorkpackagesOptions.noOption')"
      @update:model-value="updateMainFilterObject()"
    >
      <template #selection="{ item, index }">
        <span v-if="index < 1" class="selected-items">{{ item.title }} &nbsp;</span>
        <span v-if="index === 1" class="text-grey text-caption">
          (+{{ selectedCdts.length - 1 }} {{ $t('workpackagePage.splitWorkpackages.moreCDTs') }})
        </span>
      </template>
      <template #prepend-item>
        <div class="sticky">
          <div class="d-flex justify-space-around align-center pt-2">
            <v-btn class="button-link" variant="text" @click="selectAll">
              <v-icon size="16"> mdi-check </v-icon>
              <span>{{ $t('actions.selectAll') }}</span>
            </v-btn>
            <v-btn class="button-link" variant="text" @click="selectNone">
              <v-icon size="16"> mdi-checkbox-blank-outline </v-icon>
              <span>{{ $t('actions.selectNone') }}</span>
            </v-btn>
          </div>
          <v-divider class="mt-2" />
        </div>
      </template>
    </v-select>
  </v-row>
</template>

<script>
import { nextTick } from 'vue';
import { map, uniq, compact, get, sortBy, cloneDeep } from 'lodash';
import filterTypeEnum from '@enums/wp-filter-types';
import { mapState, mapActions } from 'pinia';
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useToolDataStore } from '@/stores/tool-data';

export default {
  props: {
    products: {
      type: Array,
      required: true,
    },
  },
  data: () => ({ selectedCdts: [] }),

  computed: {
    ...mapState(useWorkpackagesStore, [
      'selectedWorkpackage',
      'getProductHierarchy',
      'hierarchyDepth',
    ]),
    ...mapState(useToolDataStore, ['productHierarchy']),

    categoryProductMap() {
      const catProdMap = {};
      this.products.forEach(({ productKey, productCategoryKey }) => {
        if (catProdMap[productCategoryKey]) {
          catProdMap[productCategoryKey].push(productKey);
        } else {
          catProdMap[productCategoryKey] = [productKey];
        }
      });
      return catProdMap;
    },

    // Remove all undefined or falsy values, sort and just pluck the productCategoryKey field
    cdts() {
      if (this.products.length > 0) {
        return [...uniq(compact(map(this.products, 'productCategoryKey')))];
      }
      return [];
    },

    cdtItems() {
      const cdts = this.cdts;
      const hierarchy = this.productHierarchy;
      const items = compact(
        map(cdts, key => {
          // Attach product counts to the description
          const category = cloneDeep(get(hierarchy, key));

          if (category) {
            category.description = category[this.descriptionKey].concat(
              ` (${this.categoryProductMap[category.productCategoryKey].length} ${this.$t(
                'workpackagePage.splitWorkpackages.products'
              )})`
            );
          }
          return category;
        })
      );
      return [...sortBy(items, 'description')];
    },

    descriptionKey() {
      return `productCategory${this.hierarchyDepth}Description`;
    },
  },

  watch: {
    // When the CDTs array gets initialised, we should select all by default.
    products() {
      nextTick(() => {
        this.selectAll();
        this.$emit('updateMainFilterObject', {
          type: filterTypeEnum.cdt,
          update: this.selectedCdts,
          isInit: true,
        });
      });
    },
    selectedWorkpackage() {
      this.init();
    },
  },
  created() {
    this.init();
  },
  methods: {
    ...mapActions(useWorkpackagesStore, ['getProductHierarchyDepth']),
    ...mapActions(useToolDataStore, ['fetchProductCategories']),

    selectAll() {
      this.selectedCdts = [...this.cdts];
    },

    selectNone() {
      this.selectedCdts = [];
      this.updateMainFilterObject();
    },

    async updateMainFilterObject() {
      this.$emit('updateMainFilterObject', {
        type: filterTypeEnum.cdt,
        update: this.selectedCdts,
      });
    },

    async init() {
      await this.getProductHierarchyDepth();
      this.fetchProductsCategoriesInformation();
    },

    async fetchProductsCategoriesInformation() {
      const pick = ['productCategoryKey', this.descriptionKey];
      this.fetchProductCategories({ pick });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

:deep(.v-list) {
  position: relative;
}

.sticky {
  position: sticky;
  background-color: $canvas-header-white-background;
  top: 0;
}

.filter-box {
  align-items: center;

  span {
    padding-left: 0;
  }
}

.selected-items {
  padding-left: 5px !important;
  max-width: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
